import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Rocket, Loader2, CheckCircle2, Circle, ExternalLink, Copy, AlertCircle, MailPlus, Facebook, Sparkles, Lock,
} from "lucide-react";
import LaunchPortalSensitiveDrawer from "@/components/franchisee/LaunchPortalSensitiveDrawer";

/**
 * FranchiseeLaunchPortalPanel — HQ-side admin surface for the Launch
 * Portal V1. See ``/app/backend/launch_portal_routes.py`` for the DB
 * shape and the eight-section brief.
 *
 * Two responsibilities in Commit 1:
 *   1. Let Paul/Sandra prep the HQ-side fields for each section
 *      (email guidance text, credentials, Facebook links, comfort-zone
 *      join link).
 *   2. Show a lightweight RED/GREEN completion pill on the seven
 *      actionable sections + a prominent FINALISE LAUNCH PORTAL
 *      action at the bottom.
 *
 * Website Listing is intentionally rendered as an informational block
 * with no pill — the brief §7 explicitly excludes it from completion
 * status.
 *
 * The franchisee-facing form submissions arrive in Commit 2 and this
 * panel will start rendering their read-back inside each section.
 */
export default function FranchiseeLaunchPortalPanel({ franchisee }) {
  const [loading, setLoading] = useState(true);
  const [lp, setLp] = useState(null);
  const [completion, setCompletion] = useState({});
  const [flexChoice, setFlexChoice] = useState(null);
  const [firstContractSignedAt, setFirstContractSignedAt] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [finaliseBusy, setFinaliseBusy] = useState(false);
  const [sensitiveOpen, setSensitiveOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await api.get(
        `/admin/franchisees/${franchisee.id}/launch-portal`
      );
      setLp(data.launch_portal);
      setCompletion(data.completion || {});
      setFlexChoice(data.flex_payment_choice || null);
      setFirstContractSignedAt(data.first_contract_signed_at || null);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || "Could not load Launch Portal.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [franchisee.id]);

  const patchHq = async (fields) => {
    setSaveError("");
    try {
      const { data } = await api.patch(
        `/admin/franchisees/${franchisee.id}/launch-portal/hq`,
        fields
      );
      setLp(data.launch_portal);
      setCompletion(data.completion || {});
    } catch (e) {
      setSaveError(e?.response?.data?.detail || "Save failed.");
    }
  };

  const finalise = async () => {
    if (!window.confirm(
      "Finalise the Launch Portal? The franchisee will see the LAUNCH sections on their existing Discovery URL immediately after this."
    )) return;
    setFinaliseBusy(true);
    setSaveError("");
    try {
      await api.post(`/admin/franchisees/${franchisee.id}/launch-portal/finalise`);
      await load();
    } catch (e) {
      setSaveError(e?.response?.data?.detail || "Finalise failed.");
    } finally {
      setFinaliseBusy(false);
    }
  };

  // Per-franchisee Flex documents opt-in — independent from the
  // Discovery Portal contact-level toggle. When ON the two Flex PDFs
  // (Payment Structure + Addendum Agreement) render inside the
  // Launch Portal's Contract Details section. Default OFF.
  const toggleFlex = async (next) => {
    setSaveError("");
    try {
      await api.patch(
        `/admin/franchisees/${franchisee.id}/launch-portal/flex`,
        { show_flex: !!next },
      );
      await load();
    } catch (e) {
      setSaveError(e?.response?.data?.detail || "Flex toggle failed.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-stone-500 py-6 justify-center" data-testid="launch-portal-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading Launch Portal…
      </div>
    );
  }
  if (loadError && !lp) {
    return <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="launch-portal-error">{loadError}</div>;
  }
  if (!lp) return null;

  const hq = lp.hq || {};
  const emailSetup = hq.email_setup || {};
  // HQ tickbox state — one boolean per section Sam explicitly asked
  // for. Tolerates missing (older docs) by defaulting to false.
  const hqChecks = hq.hq_checks || {};
  const hqCheckProps = (key) => ({
    checked: !!hqChecks[key],
    onToggle: (next) => patchHq({ hq_checks: { [key]: !!next } }),
  });
  const finalised = !!lp.finalised_at;

  return (
    <div className="space-y-6" data-testid="franchisee-launch-portal-panel">
      {firstContractSignedAt && (
        <div
          className="rounded-xl border border-sky-300 bg-sky-50 p-3 flex items-center gap-2 text-sm text-sky-900"
          data-testid="launch-portal-first-contract-signed-banner"
        >
          <CheckCircle2 className="w-4 h-4" />
          <span>
            <b>First contract signed on {formatDT(firstContractSignedAt)}</b>
            {" "}— enable full portal when ready.
          </span>
        </div>
      )}
      {franchisee?.launch_portal_invite_issue && !franchisee?.launch_portal_invited_at && (
        <div
          className="rounded-xl border border-red-300 bg-red-50 p-3 flex items-center gap-2 text-sm text-red-900"
          data-testid="launch-portal-invite-issue-banner"
        >
          <AlertCircle className="w-4 h-4" />
          <span>
            <b>Welcome email not sent.</b> No active Discovery Portal link was found at
            conversion, so no Launch Portal URL could be emailed. Check the contact&apos;s
            Discovery Portal, then send the welcome manually.
          </span>
        </div>
      )}
      {finalised ? (
        <div
          className="rounded-xl border border-emerald-300 bg-emerald-50 p-3 flex items-center gap-2 text-sm text-emerald-900"
          data-testid="launch-portal-finalised-banner"
        >
          <Rocket className="w-4 h-4" />
          <span>
            <b>Launch Portal is live.</b>{" "}
            {lp.finalised_by === "auto: conversion"
              ? <>Activated automatically at conversion on {formatDT(lp.finalised_at)}.</>
              : <>Finalised {formatDT(lp.finalised_at)} by {lp.finalised_by || "admin"}.</>}
          </span>
        </div>
      ) : (
        <div
          className="rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-center gap-2 text-sm text-amber-900"
          data-testid="launch-portal-draft-banner"
        >
          <AlertCircle className="w-4 h-4" />
          <span>
            Not yet activated. New conversions activate automatically; this
            record pre-dates that. Click <b> Finalise Launch Portal </b> to expose
            the LAUNCH sections on the franchisee&apos;s existing Discovery URL.
          </span>
        </div>
      )}

      {saveError && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2">{saveError}</div>
      )}

      <div
        className="rounded-xl border border-stone-200 bg-white p-4"
        data-testid="launch-flex-toggle-row"
      >
        <label className="flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            className="mt-0.5 w-4 h-4 rounded border-stone-400 text-stone-900 focus:ring-stone-900"
            checked={!!lp.show_flex_launch}
            onChange={(e) => toggleFlex(e.target.checked)}
            data-testid="launch-flex-toggle"
          />
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-wider text-stone-900">
              Show Flex Documents in Launch Portal
            </div>
            <div className="text-[11px] text-stone-500 mt-0.5">
              When enabled, the Contract Details section on this franchisee&apos;s
              Launch Portal shows two extra sample PDFs — Flex Payment Structure and
              Flex Addendum Agreement — pulled from
              <span className="font-mono text-[10px] bg-stone-100 px-1 py-0.5 rounded ml-1">All Files &gt; Admin &gt; Flex</span>.
              Independent from the Discovery Portal Flex toggle. Default OFF.
            </div>
          </div>
        </label>
      </div>

      <SectionBlock
        title="Contract Details"
        completion={completion.contract_details}
        pending="Waiting on franchisee submission."
        testid="section-contract-details"
      >
        {lp.contract_details ? (
          <>
            <ReadbackKV data={lp.contract_details} keys={[
              ["full_name", "Full Name"],
              ["full_address", "Full Address"],
              ["mobile", "Mobile"],
              ["submitted_at", "Submitted"],
            ]} />
            {/*
              Feb-2026 rev 5 — explicit HQ read-back rows for the two
              Launch-Portal-only contract-details fields the applicant
              picks. Kept in this section (and NOT rendered elsewhere
              in Admin) so HQ has a single source of truth for what
              the applicant chose.

              * Contract term (2 or 3 years) IS behavioural — this is
                the value POST /api/contracts falls back to when HQ
                omits ``contract_term_years`` on the request body, so
                it drives the generated contract's term, its wording
                and its calculated end date.
              * Franchise fee structure (CLASSIC / FLEX) is HQ-view
                / informational only — it does NOT affect the
                contract, fees, payment choices or any downstream
                workflow. The note under the row spells this out so
                Admin never mistakes it for a switch.
            */}
            <dl
              className="text-sm space-y-1 mt-3 pt-3 border-t border-stone-200"
              data-testid="cd-hq-readback"
            >
              <div className="flex gap-2" data-testid="cd-hq-row-term">
                <dt className="text-stone-500 uppercase tracking-widest text-[10px] font-bold w-48 shrink-0 mt-1">
                  Contract term
                </dt>
                <dd
                  className="text-stone-900 tabular-nums"
                  data-testid="cd-hq-row-term-value"
                >
                  {lp.contract_details.contract_term_years === 2
                    ? "2 years"
                    : lp.contract_details.contract_term_years === 3
                    ? "3 years"
                    : <span className="text-stone-500">Not selected</span>}
                </dd>
              </div>
              <div className="flex gap-2" data-testid="cd-hq-row-fee-structure">
                <dt className="text-stone-500 uppercase tracking-widest text-[10px] font-bold w-48 shrink-0 mt-1">
                  Franchise fee structure
                </dt>
                <dd
                  className="text-stone-900 flex-1"
                  data-testid="cd-hq-row-fee-structure-value"
                >
                  <FeeStructureEditor
                    value={lp.contract_details.franchise_fee_structure}
                    onSave={async (next) => {
                      try {
                        const { data } = await api.patch(
                          `/admin/franchisees/${franchisee.id}/launch-portal/contract-details`,
                          { franchise_fee_structure: next },
                        );
                        setLp(data.launch_portal);
                        setCompletion(data.completion || {});
                      } catch (e) {
                        setSaveError(e?.response?.data?.detail || "Save failed.");
                        throw e;
                      }
                    }}
                  />
                </dd>
              </div>
              <p
                className="text-[11px] text-stone-500 pt-1 pl-[13rem]"
                data-testid="cd-hq-fee-structure-note"
              >
                Drives the Fee Structure column on the Franchises / Licences overview. Defaults to CLASSIC for franchisees who signed before the Launch Portal collected a Payment Choice; HQ can change it here at any time.
              </p>
            </dl>
          </>
        ) : (
          <div className="space-y-3" data-testid="cd-hq-not-yet-submitted">
            <em className="text-stone-500 text-sm block">Not yet submitted by the franchisee.</em>
            <div className="pt-3 border-t border-stone-200">
              <div className="flex gap-2" data-testid="cd-hq-row-fee-structure">
                <dt className="text-stone-500 uppercase tracking-widest text-[10px] font-bold w-48 shrink-0 mt-1">
                  Franchise fee structure
                </dt>
                <dd className="text-stone-900 flex-1" data-testid="cd-hq-row-fee-structure-value">
                  <FeeStructureEditor
                    value={null}
                    onSave={async (next) => {
                      try {
                        const { data } = await api.patch(
                          `/admin/franchisees/${franchisee.id}/launch-portal/contract-details`,
                          { franchise_fee_structure: next },
                        );
                        setLp(data.launch_portal);
                        setCompletion(data.completion || {});
                      } catch (e) {
                        setSaveError(e?.response?.data?.detail || "Save failed.");
                        throw e;
                      }
                    }}
                  />
                </dd>
              </div>
              <p className="text-[11px] text-stone-500 pt-1 pl-[13rem]">
                Drives the Fee Structure column on the Franchises / Licences overview. Defaults to CLASSIC until set.
              </p>
            </div>
          </div>
        )}
      </SectionBlock>

      <SectionBlock
        title="Your Franchise Name"
        completion={completion.franchise_name}
        pending="Waiting on franchisee submission."
        testid="section-franchise-name"
      >
        {lp.franchise_name?.submitted_at ? (
          <div className="space-y-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">
                Preferred (first) option
              </div>
              <div
                className="text-sm text-stone-900 font-bold"
                data-testid="lp-franchise-name-preferred"
              >
                {(lp.franchise_name.options || [])[0] || "—"}
              </div>
            </div>
            {(lp.franchise_name.options || []).length > 1 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">
                  Other options
                </div>
                <ul
                  className="list-disc pl-5 text-sm text-stone-800 space-y-0.5"
                  data-testid="lp-franchise-name-others"
                >
                  {(lp.franchise_name.options || []).slice(1).map((opt, i) => (
                    <li key={i}>{opt}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="text-xs text-stone-500 pt-1">
              Submitted {formatDT(lp.franchise_name.submitted_at)}. HQ agrees the
              final name offline before launch.
            </div>
          </div>
        ) : (
          <em className="text-stone-500 text-sm">Not yet submitted by the franchisee.</em>
        )}
      </SectionBlock>

      <SectionBlock
        title="Payment Choice"
        completion={null}
        testid="section-flex-payment-choice"
      >
        {flexChoice ? (
          <div className="space-y-1">
            <div className="text-sm text-stone-900 font-bold" data-testid="lp-flex-choice-agreement">
              {flexChoice.agreement_label} — {flexChoice.repay_label} — {flexChoice.cadence_label}
            </div>
            <div className="text-xs text-stone-700" data-testid="lp-flex-choice-detail">
              {flexChoice.detail_label}
            </div>
            {flexChoice.saved_at && (
              <div className="text-xs text-stone-500 pt-0.5" data-testid="lp-flex-choice-saved-at">
                Last saved {formatDT(flexChoice.saved_at)}
              </div>
            )}
          </div>
        ) : (
          <em className="text-stone-500 text-sm">Franchisee has not selected a payment option yet.</em>
        )}
      </SectionBlock>

      <SectionBlock
        title="Territory Preparation"
        completion={completion.territory_confirmation}
        pending="Waiting on franchisee confirmation."
        testid="section-territory-confirmation"
        hqCheck={hqCheckProps("territory_preparation")}
      >
        {lp.territory_confirmation?.confirmed ? (
          <div className="text-sm text-stone-800">
            Confirmed on{" "}
            <span className="tabular-nums font-bold">
              {formatDT(lp.territory_confirmation.confirmation_date)}
            </span>
          </div>
        ) : (
          <em className="text-stone-500 text-sm">Not yet confirmed.</em>
        )}
        <div className="text-xs text-stone-500 mt-2">
          Territory is reused from the Discovery Portal above. No separate map
          is created for the Launch Portal.
        </div>
      </SectionBlock>

      <SectionBlock
        title="Email Account"
        completion={completion.email_account}
        pending={
          lp.email_account
            ? "Address reserved — enter the mailbox password below."
            : "Waiting on franchisee to reserve a @creativemojo.co.uk address."
        }
        testid="section-email-account"
        hqCheck={hqCheckProps("email_account")}
      >
        <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500 block mb-1">
          Guidance shown above the franchisee&apos;s email selection
        </label>
        <textarea
          rows={2}
          value={hq.email_guidance_text || ""}
          onBlur={(e) => patchHq({ email_guidance_text: e.target.value })}
          onChange={(e) => setLp((d) => ({
            ...d,
            hq: { ...d.hq, email_guidance_text: e.target.value },
          }))}
          data-testid="lp-email-guidance-input"
          className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white focus:outline-none focus:border-stone-900"
          placeholder="e.g. We already have a sara@ email address currently in use, so perhaps you'd prefer sara.smith@creativemojo.co.uk."
        />

        {lp.email_account?.email_address ? (
          <div className="mt-3 p-2 bg-stone-50 border border-stone-200 rounded-lg text-sm">
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">
              Reserved by franchisee
            </div>
            <div className="tabular-nums" data-testid="lp-email-reserved">
              {lp.email_account.email_address}
            </div>
          </div>
        ) : (
          <em className="text-stone-500 text-xs italic block mt-2">
            No address reserved yet.
          </em>
        )}

        {/* HQ credentials — only editable once an address is reserved. */}
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
          <LabeledInput
            label="Email Address"
            value={emailSetup.email_address || lp.email_account?.email_address || ""}
            onSave={(v) => patchHq({ email_setup: { email_address: v } })}
            testid="lp-email-setup-email"
          />
          <LabeledInput
            label="Username"
            value={emailSetup.username || lp.email_account?.email_address || ""}
            onSave={(v) => patchHq({ email_setup: { username: v } })}
            testid="lp-email-setup-username"
          />
          <LabeledInput
            label="Password"
            value={emailSetup.password === "***" ? "" : (emailSetup.password || "")}
            placeholder={emailSetup.password === "***" ? "(set — leave blank to keep)" : ""}
            onSave={(v) => v && patchHq({ email_setup: { password: v } })}
            testid="lp-email-setup-password"
          />
        </div>
        <div className="text-xs text-stone-500 mt-1">
          The password is stored server-side and shown to the franchisee once
          this section is complete. It&apos;s never returned on the general admin GET.
        </div>
      </SectionBlock>

      <SectionBlock
        title="Personalised Facebook Page Setup"
        completion={completion.facebook_setup}
        pending="Waiting on franchisee submission."
        testid="section-facebook"
        hqCheck={hqCheckProps("facebook_page")}
      >
        {lp.facebook?.facebook_login_email && (
          <ReadbackKV data={lp.facebook} keys={[
            ["facebook_login_email", "Franchisee Facebook Login Email"],
            ["submitted_at", "Submitted"],
          ]} />
        )}
        <LabeledInput
          label="Facebook / Meta Invitation Link"
          value={hq.facebook_invitation_link || ""}
          onSave={(v) => patchHq({ facebook_invitation_link: v })}
          testid="lp-fb-invite-link"
          placeholder="Paste the Meta business invitation URL"
        />
        <LabeledInput
          label="Creative Mojo Facebook Page URL"
          value={hq.creative_mojo_facebook_page_url || ""}
          onSave={(v) => patchHq({ creative_mojo_facebook_page_url: v })}
          testid="lp-fb-page-url"
          placeholder="https://facebook.com/…"
        />
      </SectionBlock>

      <SectionBlock
        title="Website Listing"
        completion={null}
        testid="section-website-listing"
        pending={null}
      >
        <div className="text-sm text-stone-700 leading-relaxed">
          Informational only — the franchisee sees a message about their profile
          being added to the Creative Mojo website map system.
          <div className="text-xs text-stone-500 italic mt-2">
            No completion status per the V1 brief.
          </div>
        </div>
      </SectionBlock>

      <SectionBlock
        title="DBS"
        completion={completion.dbs}
        pending="Waiting on franchisee submission."
        testid="section-dbs"
        hqCheck={hqCheckProps("dbs")}
      >
        {lp.dbs?.submitted_at ? (
          <div className="text-sm text-stone-800">
            <b>Submitted</b> on {formatDT(lp.dbs.submitted_at)}. Immutable — view
            details via the Paul/Sandra-only DBS drawer.
          </div>
        ) : (
          <em className="text-stone-500 text-sm">
            Not yet submitted. The full DBS form is rendered publicly on the
            franchisee&apos;s Launch Portal.
          </em>
        )}
      </SectionBlock>

      <SectionBlock
        title="Direct Debit"
        completion={completion.direct_debit}
        pending="Waiting on franchisee submission."
        testid="section-direct-debit"
        hqCheck={hqCheckProps("direct_debit")}
      >
        {lp.direct_debit?.submitted_at ? (
          <div className="text-sm text-stone-800">
            <b>Submitted</b> on {formatDT(lp.direct_debit.submitted_at)}.
            Immutable — sort code + account number available in the Paul/Sandra-only
            drawer.
          </div>
        ) : (
          <em className="text-stone-500 text-sm">Not yet submitted.</em>
        )}
      </SectionBlock>

      <SectionBlock
        title="Creative Mojo Comfort Zone"
        completion={completion.comfort_zone}
        pending="Paste the closed-group join link so the franchisee has a fallback."
        testid="section-comfort-zone"
        hqCheck={hqCheckProps("comfort_zone")}
      >
        <LabeledInput
          label="Comfort Zone Join Link"
          value={hq.comfort_zone_join_link || ""}
          onSave={(v) => patchHq({ comfort_zone_join_link: v })}
          testid="lp-comfort-zone-link"
          placeholder="https://facebook.com/groups/…"
        />
      </SectionBlock>

      <SectionBlock
        title="Apron Colour"
        completion={completion.apron_colour}
        pending="Waiting on franchisee submission."
        testid="section-apron-colour"
        hqCheck={hqCheckProps("apron_colour")}
      >
        {lp.apron_colour?.submitted_at ? (
          <div className="space-y-1">
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">
              Chosen colour
            </div>
            <div className="text-sm text-stone-900 font-bold" data-testid="lp-apron-colour-chosen">
              {lp.apron_colour.colour}
            </div>
            <div className="text-xs text-stone-500 pt-1">
              Submitted {formatDT(lp.apron_colour.submitted_at)}.
            </div>
          </div>
        ) : (
          <em className="text-stone-500 text-sm">Not yet submitted by the franchisee.</em>
        )}
      </SectionBlock>

      <div className="pt-2 border-t border-stone-200">
        {(completion.dbs || completion.direct_debit) && (
          <div className="mb-3">
            <button
              onClick={() => setSensitiveOpen(true)}
              data-testid="launch-portal-sensitive-open"
              className="w-full px-4 py-2.5 text-sm font-bold uppercase tracking-wider border border-amber-400 bg-amber-50 hover:bg-amber-100 text-amber-900 rounded-lg flex items-center justify-center gap-2"
            >
              <Lock className="w-4 h-4" />
              View Sensitive Data (Paul/Sandra only)
            </button>
            <div className="text-xs text-stone-500 mt-1 italic">
              DBS documents and bank details are stored under a restricted scope
              and are only viewable by Paul or Sandra. Every open is logged.
            </div>
          </div>
        )}
        {finalised ? (
          <div className="text-sm text-stone-600 italic" data-testid="launch-portal-already-finalised">
            Launch Portal live since {formatDT(lp.finalised_at)} — the
            franchisee has access.
          </div>
        ) : (
          <button
            onClick={finalise}
            disabled={finaliseBusy}
            data-testid="launch-portal-finalise"
            className="w-full px-4 py-3 text-sm font-bold uppercase tracking-widest border-2 border-stone-950 bg-stone-950 hover:bg-stone-800 text-[#dddd16] rounded-lg flex items-center justify-center gap-2 disabled:opacity-40"
          >
            {finaliseBusy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Rocket className="w-4 h-4" />
            )}
            {finaliseBusy ? "Finalising…" : "Finalise Launch Portal"}
          </button>
        )}
        <div className="text-xs text-stone-500 mt-2">
          Finalising exposes the YOUR LAUNCH PORTAL navigation on the
          franchisee&apos;s existing Discovery URL. It does <b>not</b> grant access
          to the main Franchise Hub.
        </div>
      </div>
      {sensitiveOpen && (
        <LaunchPortalSensitiveDrawer
          franchisee={franchisee}
          onClose={() => setSensitiveOpen(false)}
        />
      )}
    </div>
  );
}


function SectionBlock({ title, completion, pending, children, testid, hqCheck }) {
  const showPill = completion !== null && completion !== undefined;
  const done = !!completion;
  const hqDone = !!hqCheck?.checked;
  // When HQ has ticked this section off, wash the whole panel in a
  // very light green so it's visually obvious at a glance which
  // sections are done from HQ's side — independent of client status.
  const panelBg = hqDone ? "bg-emerald-50/70" : "bg-white";
  const headerBg = hqDone ? "bg-emerald-100/60" : "bg-stone-50/70";
  return (
    <div
      className={
        "rounded-xl border shadow-sm " +
        (hqDone ? "border-emerald-300 " : "border-stone-200 ") +
        panelBg
      }
      data-testid={testid}
    >
      <div className={"flex items-center justify-between gap-3 px-4 py-3 border-b rounded-t-xl " +
        (hqDone ? "border-emerald-200 " : "border-stone-200 ") + headerBg}>
        <h4 className="font-display text-base font-black text-stone-950 tracking-tight">
          {title}
        </h4>
        <div className="flex items-center gap-2">
          {showPill && (
            <span
              className={
                "text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border flex items-center gap-1 " +
                (done
                  ? "bg-emerald-50 text-emerald-800 border-emerald-300"
                  : "bg-red-50 text-red-800 border-red-300")
              }
              data-testid={`${testid}-pill`}
            >
              {done ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
              {done ? "Client Complete" : "Client Outstanding"}
            </span>
          )}
          {hqCheck && (
            <label
              className={
                "cursor-pointer select-none text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border flex items-center gap-1 transition-colors " +
                (hqDone
                  ? "bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700"
                  : "bg-white text-stone-700 border-stone-400 hover:bg-stone-100")
              }
              data-testid={`${testid}-hq-check-label`}
            >
              <input
                type="checkbox"
                checked={hqDone}
                onChange={() => hqCheck.onToggle(!hqDone)}
                className="w-3 h-3 accent-emerald-700"
                data-testid={`${testid}-hq-check`}
              />
              {hqDone ? "HQ Complete" : "HQ Outstanding"}
            </label>
          )}
        </div>
      </div>
      <div className="p-4 space-y-2">
        {children}
        {showPill && !done && pending && (
          <div className="text-xs text-stone-500 italic pt-1">{pending}</div>
        )}
      </div>
    </div>
  );
}

function LabeledInput({ label, value, onSave, placeholder, testid }) {
  const [local, setLocal] = useState(value || "");
  useEffect(() => { setLocal(value || ""); }, [value]);
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500 block mb-1">
        {label}
      </label>
      <input
        type="text"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={() => local !== (value || "") && onSave(local.trim() || null)}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg bg-white focus:outline-none focus:border-stone-900"
      />
    </div>
  );
}

function FeeStructureEditor({ value, onSave }) {
  const normalised = value === "classic" || value === "flex" ? value : null;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(normalised || "classic");
  const [busy, setBusy] = useState(false);
  const label = normalised === "classic"
    ? "CLASSIC"
    : normalised === "flex"
      ? "FLEX"
      : null;

  if (!editing) {
    return (
      <div className="flex items-center gap-3 flex-wrap">
        {label ? (
          <span
            className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-md ${
              normalised === "flex"
                ? "bg-indigo-100 text-indigo-800 border-indigo-300"
                : "bg-stone-100 text-stone-800 border-stone-300"
            }`}
            data-testid="cd-hq-fee-structure-chip"
          >
            {label}
          </span>
        ) : (
          <span className="text-stone-500 text-sm" data-testid="cd-hq-fee-structure-empty">
            Not selected · defaults to CLASSIC on overview
          </span>
        )}
        <button
          type="button"
          onClick={() => { setDraft(normalised || "classic"); setEditing(true); }}
          className="text-[11px] font-bold uppercase tracking-wider text-stone-600 hover:text-stone-950 underline-offset-2 hover:underline"
          data-testid="cd-hq-fee-structure-change"
        >
          Change
        </button>
      </div>
    );
  }

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch { /* parent surfaces the error */ } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="cd-hq-fee-structure-editor">
      <label className="flex items-center gap-1.5 text-sm">
        <input
          type="radio"
          name="hq-fee-structure"
          value="classic"
          checked={draft === "classic"}
          onChange={() => setDraft("classic")}
          disabled={busy}
          data-testid="cd-hq-fee-structure-radio-classic"
        />
        CLASSIC
      </label>
      <label className="flex items-center gap-1.5 text-sm">
        <input
          type="radio"
          name="hq-fee-structure"
          value="flex"
          checked={draft === "flex"}
          onChange={() => setDraft("flex")}
          disabled={busy}
          data-testid="cd-hq-fee-structure-radio-flex"
        />
        FLEX
      </label>
      <button
        type="button"
        onClick={save}
        disabled={busy || draft === normalised}
        className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-stone-950 text-[#dddd16] rounded-lg disabled:opacity-40"
        data-testid="cd-hq-fee-structure-save"
      >
        {busy ? "Saving…" : "Save"}
      </button>
      <button
        type="button"
        onClick={() => { setEditing(false); setDraft(normalised || "classic"); }}
        disabled={busy}
        className="text-[11px] font-bold uppercase tracking-wider text-stone-500 hover:text-stone-800"
        data-testid="cd-hq-fee-structure-cancel"
      >
        Cancel
      </button>
    </div>
  );
}

// ----------------------------------------------------------------------------
// FeeStructureEditor — inline HQ control for franchise_fee_structure.
//
// Feb-2026: the Franchises / Licences overview now surfaces each
// franchisee's CLASSIC/FLEX fee structure using this value, so HQ needs
// a way to flip it manually (either because the franchisee signed before
// the Launch Portal collected a Payment Choice, or because the business
// has agreed a change offline). Collapsed by default so the readback
// stays tidy; opens into a two-radio form on "Change".
// ----------------------------------------------------------------------------
function ReadbackKV({ data, keys }) {
  return (
    <dl className="text-sm space-y-1">
      {keys.map(([k, label]) => {
        const val = data?.[k];
        if (!val) return null;
        return (
          <div key={k} className="flex gap-2">
            <dt className="text-stone-500 uppercase tracking-widest text-[10px] font-bold w-48 shrink-0 mt-1">{label}</dt>
            <dd className="text-stone-900 tabular-nums">
              {typeof val === "string" && /^\d{4}-\d{2}-\d{2}T/.test(val) ? formatDT(val) : String(val)}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function formatDT(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
